<template>
    <div class="pt-6">
    <v-row v-if="effectiveChainId">
      <v-col cols="12" md="8">
        <v-form ref='form' v-if="form.guidelines">
          <v-row>
            <v-col cols="12" md="8">
              <d-wysiwyg-editor
                v-model.trim="form.guidelines"
              ></d-wysiwyg-editor>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" md="8">
        <div v-text="$t('guidelines.selectDealerHint')"></div>
      </v-col>
    </v-row>
    <v-row v-if="effectiveChainId">
      <v-col cols="12" md="8" class="d-save-col">
        <div>
          <v-btn
            v-text="$t('save')"
            :disabled="!isModelChanged()"
            class="d-btn-1 text-uppercase"
            width="120"
            depressed
            @click="saveChanges"
          ></v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import pageMixin from '@/mixins/page.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

import DWysiwygEditor from '@/components/DWysiwygEditor'


export default {
  components: {
    DWysiwygEditor
  },

  watch: {
    currentDealerId (newId, oldId) {
      if (newId !== oldId && this.currentUserIsSuperAdmin) {
        this.initForm()
      }
    },

    // Reload the entire page when the selected chain changes so the app reinitializes
    currentChainId (newId, oldId) {
      if (newId !== oldId) {
        window.location.reload()
      }
    }
  },


  mixins: [
    pageMixin,
    snackbarMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      form: {
        guidelines: null
      }
    }
  },


  computed: {
    ...mapGetters('dealer', ['currentDealerId', 'shortDealerById']),
    ...mapGetters('chain', ['currentChainId', 'currentChain']),
    ...mapGetters('user', ['currentUserIsSuperAdmin']),

    effectiveChainId () {
      if (this.currentUserIsSuperAdmin) {
        return this.shortDealerById(this.currentDealerId)?.chainId
      }
      return this.currentChainId
    },

    rulesToApply () {
      const rule = this.rule

      return {
        guidelines: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('guidelines', ['updateGuidelines']),
    ...mapActions('chain', ['getChainById']),

    async initForm () {
      if (!this.effectiveChainId) return

      const currentChain = await this.getChainById(this.effectiveChainId)
      let guidelines = currentChain?.guidelines

      // wysiwyg editor wraps the message with <p> tag (if it's missing)
      // this change affects isModelChanged() value, so the form behaves like it was edited, while the user did nothing
      // the fix below helps to avoid the user confusing :)
      const guidelinesRegex = /^(<p>)[\s\S]*(<\/p>)$/g
      guidelines = guidelines || ''
      if (!guidelinesRegex.test(guidelines)) {
        guidelines = `<p>${guidelines}</p>`
      }

      this.form.guidelines = guidelines

      this.initWatchedModel(this.form)
      this.resetValidation()
    },

    async saveChanges () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        await this.updateGuidelines({ chainId: this.effectiveChainId, guidelines: this.form.guidelines })
        this.initWatchedModel(this.form)
        this.showSnackbarSuccess(this.$t('template.templatesUpdated'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  async created () {
    // When we land on the Settings->Guidelines tab, trigger a one-time full-page reload
    // per chain to ensure the app/global state is fully reinitialized. Use sessionStorage
    // to avoid reload loops; we store the chain id we reloaded for this session.
    try {
      const reloadKey = 'settingsGuidelinesReloadedForChain'
      const chainIdKey = this.effectiveChainId || ''
      const lastReloaded = sessionStorage.getItem(reloadKey) || ''
      if (lastReloaded !== String(chainIdKey)) {
        sessionStorage.setItem(reloadKey, String(chainIdKey))
        // perform full reload once when arriving to this tab for the given chain
        window.location.reload()
        return
      }

      this.startPageLoading()
      await this.initForm()
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  },

  beforeRouteLeave (to, from, next) {
    this.escape(next, () => next(false))
  }
}
</script>
